import React, { useState, useEffect } from "react";

import CaseDialog from "./CaseDialog";
import Content from "./Content";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InfoSidebar from "./InfoSidebar";

import {
  AdjudicationParadigm,
  CaseWithImages,
  ImageAndQuery,
  QueryDetails,
  QueryObjectType
} from "../models";

import { hasOpenQueries, hasAnsweredQueries } from "../slices/queryDetails";

import {
  faArrowLeft,
  faArrowRight,
  faClipboard,
  faEye,
  faFlag,
  faScaleBalanced
} from "@fortawesome/free-solid-svg-icons";
import SidebarPanelCase from "./SidebarPanelCase";
import styled from "styled-components";
import SidebarPanelAnnotations from "./SidebarPanelAnnotations";
import SidebarPanelQueries from "./SidebarPanelQueries";
import SidebarPanelAdjudication from "./SidebarPanelAdjudication";
import { useAppSelector } from "../hooks";
import { LoggedInUser } from "../permissions";

interface Props {
  readonly studyName: string | null;
  readonly histoCase: CaseWithImages;
  readonly selectedImage: ImageAndQuery | null;
  readonly userCanDeleteAnnotations: boolean;
  readonly queryDetails: QueryDetails;
  readonly loggedInUser: LoggedInUser;
  readonly hasAiAnnotations: boolean;
  readonly readerAcceptsAi: boolean;
}

const PanelIcon = styled.div`
  height: 50px;
  width: 50px;
  padding: 15px;
  cursor: pointer;
  margin-top: 0px;
  margin-bottom: 4px;
  transition: opacity 0.2s ease-in-out;
`;

const NotificationDot = styled.div`
  position: absolute;
  left: -13px;
  top: -3px;
  width: 11px;
  height: 11px;
  background-color: red;
  border-radius: 50%;
  border: 2px solid white;
`;

const InfoSidebarCase = ({
  studyName,
  histoCase,
  selectedImage,
  userCanDeleteAnnotations,
  queryDetails,
  loggedInUser,
  hasAiAnnotations,
  readerAcceptsAi
}: Props) => {
  const user = useAppSelector(state => state.auth.user);

  const [hideSidebar, setHideSidebar] = useState(false);

  const toggleHideSidebar = function () {
    hideSidebar ? setHideSidebar(false) : setHideSidebar(true);
  };

  const [selectedPanel, setSelectedPanel] = useState(0);
  const [hoveredPanel, setHoveredPanel] = useState(-1);

  const selectPanel = function (index: number) {
    setSelectedPanel(index);
    setHideSidebar(false);
  };

  const isNotificationQueryIcon = function () {
    return (
      hasOpenQueries(queryDetails, histoCase, selectedImage) ||
      hasAnsweredQueries(queryDetails, histoCase, selectedImage)
    );
  };

  const [notificationQueryIcon, setNotificationQueryIcon] = useState(isNotificationQueryIcon());

  useEffect(() => {
    setNotificationQueryIcon(isNotificationQueryIcon());
  }, [queryDetails]);

  // FE-06: show the Adjudication panel only for the case's adjudicator on a read-switching paradigm
  // (Forced/Hybrid). Independent behaves like a normal reader, so no panel.
  const adjudicationReads = useAppSelector(state => state.caseImageViewer.adjudicationReads);
  const studyView = useAppSelector(state => state.studies.study);
  const paradigm = "resource" in studyView ? studyView.resource.study.adjudicationParadigm : null;
  // LUC-278: System Admin / Study Admin / ISC get the same panel unblinded and read-only (the reads
  // come back with `blinded = false`) on any study with a CR, so they can see which reader produced
  // which annotations and scores — including Independent and None, where there is no adjudicator.
  const showAdjudicationPanel =
    adjudicationReads !== null &&
    (adjudicationReads.blinded
      ? paradigm === AdjudicationParadigm.Forced || paradigm === AdjudicationParadigm.Hybrid
      : adjudicationReads.reads.length > 0);

  const panels = [
    {
      // Added transform as spacing between eye icon seems strange as it's height looks smaller than the rest
      icon: (
        <FontAwesomeIcon
          icon={faEye}
          title="Overview"
          style={{ width: "100%", height: "100%", marginBottom: "-2px", transform: "scale(1.1)" }}
        />
      ),
      content: (
        <SidebarPanelCase
          studyName={studyName}
          histoCase={histoCase}
          selectedImage={selectedImage}
          loggedInUser={loggedInUser}
          hasAiAnnotations={hasAiAnnotations}
          readerAcceptsAi={readerAcceptsAi}
        ></SidebarPanelCase>
      )
    },
    {
      icon: (
        <FontAwesomeIcon
          icon={faClipboard}
          title="Annotations"
          style={{ width: "100%", height: "100%" }}
        />
      ),
      content: (
        <SidebarPanelAnnotations
          studyName={studyName}
          selectedImage={selectedImage}
          histoCase={histoCase}
          userCanDeleteAnnotations={userCanDeleteAnnotations}
          loggedInUser={loggedInUser}
        ></SidebarPanelAnnotations>
      )
    },
    {
      icon: (
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          {notificationQueryIcon ? <NotificationDot></NotificationDot> : null}
          <FontAwesomeIcon
            icon={faFlag}
            title="Queries"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      ),
      content: (
        <SidebarPanelQueries
          studyName={studyName}
          histoCase={histoCase}
          selectedImage={selectedImage}
          queryDetails={queryDetails}
          currentViewQueryObjectType={QueryObjectType.Case}
          loggedInUser={loggedInUser}
        ></SidebarPanelQueries>
      )
    },
    // FE-06: the Adjudication panel is shown when the current user is this case's adjudicator on a
    // read-switching paradigm (Forced/Hybrid), or is an oversight role (LUC-278, read-only).
    ...(showAdjudicationPanel
      ? [
          {
            icon: (
              <FontAwesomeIcon
                icon={faScaleBalanced}
                title={
                  adjudicationReads?.blinded === false ? "Central Reader Reads" : "Adjudication"
                }
                style={{ width: "100%", height: "100%" }}
              />
            ),
            content: <SidebarPanelAdjudication />
          }
        ]
      : [])
  ];

  const iconDock = (
    <div
      style={{
        height: "100%",
        width: "50px",
        borderRight: "solid 1px #ccc",
        borderLeft: "solid 1px #ccc",
        float: "left"
      }}
    >
      {panels.map((panel, index) => (
        <PanelIcon
          key={index}
          style={{
            color: selectedPanel === index ? "#FFFFFF" : "#9A9DA0",
            backgroundColor: selectedPanel === index ? "#57B7B3" : "transparent",
            opacity: hoveredPanel === index && selectedPanel !== index ? 0.8 : 1
          }}
          onClick={() => {
            selectPanel(index);
          }}
          onMouseEnter={() => setHoveredPanel(index)}
          onMouseLeave={() => setHoveredPanel(-1)}
        >
          {panel.icon}
        </PanelIcon>
      ))}
      <PanelIcon style={{ color: "#57B7B3" }} onClick={toggleHideSidebar}>
        <FontAwesomeIcon
          icon={hideSidebar ? faArrowLeft : faArrowRight}
          style={{ position: "absolute", bottom: "15px" }}
        />
      </PanelIcon>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {iconDock}
      <InfoSidebar style={{ display: hideSidebar ? "none" : "block" }}>
        <Content isLoading={"isPending" in user || "isPending" in histoCase}>
          {/* Clamp to the available panels: if the panel set shrinks (e.g. the Adjudication panel
              drops out when adjudicationReads briefly clears), fall back to the last panel rather
              than rendering blank content for a now-out-of-range index. */}
          {(panels[selectedPanel] ?? panels[panels.length - 1])?.content}
        </Content>
      </InfoSidebar>
      <CaseDialog />
    </div>
  );
};

export default InfoSidebarCase;
